import { defineComponent } from 'vue';
import theme from '../../core/theme/mixins';
import TdlTypeaheadCommon from './tdlTypeaheadCommon';
import debounce from 'lodash-es/debounce';
import { isUnderMobileWidth } from '../../core/browser';
import { mdiPlusCircle, mdiAlert, mdiKeyboard } from '@mdi/js';

export default defineComponent({
  name: 'tdl-typeahead',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    disabled: Boolean,
    inputLabel: String,
    cancelLabel: String,
    textAttribute: {
      type: String,
      default: 'text',
    },
    onlyRegularCharacters: {
      type: Boolean,
      default: false,
    },
    enabledScroll: {
      type: Boolean,
      default: false,
    },
    onlyChips: {
      type: Boolean,
      default: false,
    },
    idAttribute: {
      type: String,
      default: 'id',
    },
    fetchList: Function,
    debounce: {
      type: Number,
      default: 100,
    },
    filterList: {
      type: Function,
      default: (item) => true,
    },
    addNewItem: {
      type: Boolean,
      required: true,
    },
    minChars: {
      type: Number,
      default: 0,
    },
    addNewMessage: {
      type: String,
      default: 'Be the first one to add this',
    },
    excludedMessage: {
      type: String,
      default: 'Word unavailable in this field. Try again.',
    },
    excludedList: {
      type: Array,
      default: () => [],
    },
    softList: {
      type: Boolean,
      default: false,
    },
    softTitle: {
      type: String,
      default: 'Start typing for keywords...',
    },
    maxItems: {
      type: Number,
      default: Infinity,
    },
    disableMaxWidth: {
      type: Boolean,
      default: false,
    },
    desktopItemListFixed: Boolean,
    desktopSearchHintMode: {
      type: String,
      required: false,
      default: () => 'focus',
    },
    addItemOnKeypress: {
      type: String,
      required: false,
    },
    itemFactory: {
      type: Function,
      default: (text) => Promise.resolve({ id: text, text: text }),
    },
    addArrowDown: {
      type: Boolean,
      default: true,
    },
    forceClose: {
      type: Boolean,
      default: false,
    },
    grouped: {
      type: Boolean,
      default: false,
    },
    emptyValuesMessage: {
      type: String,
    },
    fetchNextPage: {
      default: () => Promise.resolve({}),
    },
    disableAutoFocus: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [theme, TdlTypeaheadCommon],
  data() {
    return {
      focused: false,
      selectedItems: [],
      baseList: [],
      fetchListDebounced: Function,
      fetching: [],
      mdiPlusCircle,
      mdiAlert,
      mdiKeyboard,
    };
  },
  watch: {
    modelValue(values) {
      this.selectedItems = values || [];
    },
  },
  computed: {
    isMobile() {
      return isUnderMobileWidth();
    },
    loading: function () {
      return this.fetching.length > 0;
    },
    disabledInput: function () {
      return this.selectedItems.length >= this.maxItems && this.maxItems !== 1;
    },
    classes: function () {
      return {
        'md-disabled': this.disabled,
        'md-has-value': this.selectedItems.length,
        'has-only-item': this.selectedItems.length === 1 && this.maxItems === 1,
      };
    },
    itemList: function () {
      return (this.baseList || []).filter(this.filterList);
    },
    hasItemSlot: function () {
      return !!this.$slots['item'];
    },
    hasCreateSlot: function () {
      return !!this.$slots['create'];
    },
    hasChipSlot: function () {
      return !!this.$slots['chip'];
    },
  },
  mounted() {
    if (this.modelValue) {
      this.selectedItems = this.modelValue;
    }
    this.fetchListDebounced = debounce(this.doFetchList, this.debounce, {
      leading: true,
      trailing: true,
    });
  },
  methods: {
    handleQueryChanged(newVal) {
      if (this.focused) {
        if (this.selectedItems.length < this.maxItems && newVal.length >= this.minChars) {
          this.fetchListDebounced(newVal, this.softList);
        } else {
          this.baseList = [];
        }
      }
      this.$emit('query-changed', newVal);
    },
    async handleNextPage(item) {
      const suffix = Math.random();
      const key = `${item.title}_nextPage.${suffix}`;

      this.fetching.push(key);
      if (this.fetchNextPage instanceof Function) {
        const nextPageData = await this.fetchNextPage(item);
        this.baseList = this.baseList.map((i) => {
          if (i.title === item.title) {
            return {
              title: i.title,
              values: [...i.values, ...nextPageData.data],
              pagination: nextPageData.pagination,
            };
          }
          return i;
        });
        this.fetching = this.fetching.filter((k) => k !== key);
      }
    },
    handleFocus({ focused, query }) {
      if (this.baseList?.length && this.grouped && this.fetching.length) {
        this.focused = focused;
        return;
      }
      if (focused && this.selectedItems.length < this.maxItems && query.length >= this.minChars) {
        this.baseList = [];
        this.fetchListDebounced(query, this.softList);
      } else if (focused && this.selectedItems.length < this.maxItems && query.length < this.minChars) {
        this.baseList = [];
      }
      if (!focused && query && query.trim().length > 0) {
        this.$emit('blur-dirty');
      }
      this.focused = focused;
    },
    doFetchList(query, softList = false) {
      const suffix = Math.random();
      const key = `${query}.${suffix}`;

      this.fetching.push(key);

      this.fetchList(query, softList)
        .then((response) => {
          this.baseList = response;
          this.fetching = this.fetching.filter((k) => k !== key);
          this.$nextTick(() => {
            this.$emit('item-list-changed', { itemListLength: this.itemList.length });
          });
        })
        .catch(() => {
          this.fetching = this.fetching.filter((k) => k !== key);
        });
    },
    deleteChip(item) {
      this.selectedItems = this.selectedItems.filter((selected) => this.getId(selected) !== this.getId(item));
      if (this.disableAutoFocus) {
        setTimeout(() => {
          document.activeElement.blur();
        }, 30);
      }
    },
    handleItemSelected(item) {
      const selectedAlready = !!this.selectedItems.find((selected) => this.getId(selected) === this.getId(item));
      const onlyOneSelectioon = this.maxItems === 1;

      if (onlyOneSelectioon) {
        this.selectedItems = [item];
      } else {
        if (!selectedAlready) {
          this.selectedItems.push(item);
        }
      }

      this.$emit('addItem', item);
      this.$emit('update:modelValue', this.selectedItems);
    },
    handleItemRemoved(item) {
      this.deleteChip(item);
      this.$emit('removeItem', item);
      this.$emit('update:modelValue', this.selectedItems);
    },
    // Public API
    requestFocus() {
      if (!this.focused) {
        this.$nextTick(() => {
          if (this.$refs['delegatedTypeahead']) {
            this.$refs['delegatedTypeahead'].requestFocus();
          }
        });
      }
    },
  },
});
